import {
  useCallback, useMemo,
} from 'react';
import { isResourceLoaded } from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import { uniqueValues } from '~/_shared/utils/array/array.helpers';
import { flatten } from '~/_shared/utils/collections/collections';
import { useTranslation } from '~/_shared/utils/hooks';
import { numberObjectKeys } from '~/_shared/utils/object/objectEnumerators';
import { notNull } from '~/_shared/utils/typeGuards';
import type { ChartItem } from '~/charts/chartItem/chartItem.types';
import { useBoundaryStateItem } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryStateItem';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import { useSelectAllDemographicsMap } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  createChartBoundaryStatistics, createChartMetricDemographic, createChartMetricSpreadsheetColumn,
} from '~/store/mapSettings/data/charts/mapSettingsCharts.factory';
import {
  type Chart,
  type ChartBoundaryStatistics, ChartMetricType,
  isChartBoundaryStatistics,
  isChartMetricDemographic,
  isChartMetricSpreadsheetColumn,
} from '~/store/mapSettings/data/charts/mapSettingsCharts.types';
import { useMapSettingsCharts } from '~/store/mapSettings/data/charts/useMapSettingsCharts';
import { getMatchupDataColumnName } from '~/store/matchupData/matchupData.helpers';
import { useMatchupDataSelector } from '~/store/matchupData/matchupData.selectors';
import {
  isStatsItemLoaded, isStatsItemLoading, isStatsItemSyncing,
} from '~/store/stats/stats.helpers';
import type {
  BoundaryStatsItem, StatItem,
} from '~/store/stats/stats.state';
import { useChartsStats } from '~/store/stats/useChartsStats';

type ChartWithStats = Readonly<{
  chart: Chart;
  stats: {
    statsItems: ReadonlyArray<StatItem>;
    values: ChartItem['metrics'];
  };
}>;

export const useMapPageChartItems = () => {
  const [t] = useTranslation();
  const matchupData = useMatchupDataSelector();
  const { charts: savedCharts, addOrUpdateChart } = useMapSettingsCharts();
  const demographicsMap = useSelectAllDemographicsMap();
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const { getBoundaryStateItem } = useBoundaryStateItem();

  const mergeChartBoundaryStatisticsWithStats = useCallback((
    chart: ChartBoundaryStatistics,
    stats: ReadonlyArray<BoundaryStatsItem | null>,
  ): ChartWithStats => {
    const onlyValidStats = stats.filter(notNull);
    const allMetricsBoundaryIdsForChart = uniqueValues(flatten(onlyValidStats.map((statItem) => {
      if (isResourceLoaded(statItem.data) && statItem.data.value.data?.values) {
        return numberObjectKeys(statItem.data.value.data.values);
      }
      return [];
    })));

    const boundaryIdsAndNames = allMetricsBoundaryIdsForChart.map((boundaryId, index) => [
      boundaryId,
      getBoundaryStateItem(boundaryId, chart.boundaryGroupId)?.displayName || `${t('Territory')} #${index + 1}`,
    ] as const);

    const boundaryIdsAndNamesSorted = boundaryIdsAndNames.toSorted(
      (a, b) => a[0] - b[0],
    );

    const metricsNames = onlyValidStats.map((statItem, index) => {
      if (isChartMetricSpreadsheetColumn(statItem)) {
        return getMatchupDataColumnName(matchupData, statItem.column);
      }
      if (isChartMetricDemographic(statItem)) {
        const demographic = demographicsMap.get(statItem.demographicId);
        if (demographic) {
          return demographic.name;
        }
      }
      return `${t('Metric')} #${index + 1}`;
    });

    return {
      chart,
      stats: {
        statsItems: onlyValidStats,
        values: {
          labels: boundaryIdsAndNamesSorted.map((item) => item[1]),
          barValues: onlyValidStats.map((statItem, index) => ({
            name: metricsNames[index] || `${t('Metric')} ${index + 1}`,
            values: isResourceLoaded(statItem.data)
              ? boundaryIdsAndNamesSorted.map((item) => {
                const boundaryId = item[0];
                return statItem.data.value?.data?.values[boundaryId] ?? 0;
              })
              : [],
          })),
          medians: onlyValidStats.map((statItem, index) => ({
            name: `${t('Median')} ${metricsNames[index] || `${t('Metric')} ${index + 1}`}`,
            value: isResourceLoaded(statItem.data) ? statItem.data.value?.data?.median ?? 0 : 0,
          })),
          averages: onlyValidStats.map((statItem, index) => ({
            name: `${t('Average')} ${metricsNames[index] || `${t('Metric')} ${index + 1}`}`,
            value: isResourceLoaded(statItem.data) ? statItem.data.value?.data?.avg ?? 0 : 0,
          })),
        },
      },
    };
  }, [demographicsMap, getBoundaryStateItem, matchupData, t]);

  const defaultTerritoryStats: ChartBoundaryStatistics[] = useMemo(() => (
    boundaryTerritoryGroups.map((btg) =>
      btg.settings.defaultStatistics ? createChartBoundaryStatistics({
        boundaryGroupId: btg.boundaryGroupId,
        title: getBoundaryTerritoryGroupName(btg) || t('Loading...'),
        shouldNormalize: btg.settings.defaultStatistics.length > 1,
        metrics: btg.settings.defaultStatistics.map((statistic) => {
          if (statistic.type === ChartMetricType.SpreadsheetColumn) {
            return createChartMetricSpreadsheetColumn({
              spreadsheetId: statistic.virtualSpreadsheetId,
              columnId: statistic.metric,
            });
          }
          if (statistic.type === ChartMetricType.Demographic) {
            return createChartMetricDemographic(
              '' + statistic.metric,
            );
          }
          return null;
        }).filter(notNull),
      }) : null
    ).filter(notNull)
  ), [boundaryTerritoryGroups, getBoundaryTerritoryGroupName, t]);

  const allCharts = useMemo(() => [
    ...savedCharts,
    ...defaultTerritoryStats.filter((chart) =>
      !savedCharts.some((savedChart) => (
        isChartBoundaryStatistics(savedChart)
        && savedChart.boundaryGroupId === chart.boundaryGroupId
      ))
    ),
  ], [defaultTerritoryStats, savedCharts]);

  const { chartsStats } = useChartsStats(allCharts);

  const allChartsWithStats = useMemo(() => allCharts.map((chart, index) => {
    if (isChartBoundaryStatistics(chart)) {
      return mergeChartBoundaryStatisticsWithStats(chart, chartsStats[index] || []);
    }
    return {
      chart,
      stats: {
        statsItems: [],
        values: {
          averages: [],
          barValues: [],
          labels: [],
          medians: [],
        },
      },
    };
  }), [allCharts, chartsStats, mergeChartBoundaryStatisticsWithStats]);

  const chartItems = useMemo(() => allChartsWithStats.map((chartWithStats, index) => {
    const isSyncing = chartWithStats.stats.statsItems.some(statItem => isStatsItemSyncing(statItem));
    const isLoading = chartWithStats.stats.statsItems.some(statItem => isStatsItemLoading(statItem));
    const hasError = chartWithStats.stats.statsItems.some(statItem => !isStatsItemLoading(statItem) && !isStatsItemLoaded(statItem));

    return {
      id: chartWithStats.chart.id,
      title: chartWithStats.chart.title || `${t('Chart')} #${index + 1}`,
      isSyncing,
      isOutdated: false,
      isLoading,
      hasError,
      shouldNormalize: chartWithStats.chart.shouldNormalize,
      metrics: {
        averages: chartWithStats.stats.values.averages,
        barValues: chartWithStats.stats.values.barValues,
        labels: chartWithStats.stats.values.labels,
        medians: chartWithStats.stats.values.medians,
      },
      onNormalizeToggleClick: (newValue: boolean) => {
        addOrUpdateChart({
          ...chartWithStats.chart,
          shouldNormalize: newValue,
        });
      },
    } satisfies ChartItem;
  }), [allChartsWithStats, t, addOrUpdateChart]);

  return {
    chartItems,
  };
};
